import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getFirestore, collection, query, where, getDocs, doc, setDoc } from 'firebase/firestore';
import { addAcceptedInvitation } from '../../store/slices/authSlice';
import { useTranslation } from 'react-i18next';
import './Notifications.css';
import ProfileDetail from '../ProfileDetail/ProfileDetail';
import SubscriptionOffcanvas from '../Subscription/SubscriptionOffcanvas';
import { hasActiveSubscription, hasPremiumAccess } from '../../utils/subscriptionUtils';
import InfiniteScroll from 'react-infinite-scroll-component';
import { notificationService } from '../../services/notificationService';
import moment from 'moment';
import { calculateAge } from '../../utils/profileUtils';
import { Spin } from 'antd';

const Notifications = () => {
  const [likes, setLikes] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('likes');
  const [error, setError] = useState(null);
  const [showSubscription, setShowSubscription] = useState(false);
  const [invitationsPage, setInvitationsPage] = useState(1);
  const [likesPage, setLikesPage] = useState(1);
  const [hasMoreInvitations, setHasMoreInvitations] = useState(true);
  const [hasMoreLikes, setHasMoreLikes] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [containerHeight, setContainerHeight] = useState('600px');
  const dispatch = useDispatch();
  const authState = useSelector(state => state.auth);
  const userLocation = authState.location ? [authState.location.longitude, authState.location.latitude] : null;
  const { t } = useTranslation();

  const canAcceptMoreInvitations = () => {

    // Subscribed users can accept unlimited invitations
    if (hasActiveSubscription(authState)) return true;
    
    const currentTime = Math.floor(Date.now() / 1000);
    const oneWeekAgo = currentTime - (7 * 24 * 60 * 60);
    
    // Count invitations accepted in the last week
    const recentAcceptances = authState.acceptedInvitations?.filter(
      timestamp => timestamp > oneWeekAgo
    ).length || 0;
    
    return recentAcceptances < 2;
  };

  const getTimeUntilNextInvitation = () => {
    if (hasActiveSubscription(authState)) return null;
    
    const currentTime = Math.floor(Date.now() / 1000);
    const oneWeekAgo = currentTime - (7 * 24 * 60 * 60);
    
    const acceptedInvitations = authState.acceptedInvitations?.filter(
      timestamp => timestamp > oneWeekAgo
    ) || [];
    
    if (acceptedInvitations.length === 0) return null;
    
    const oldestAcceptance = Math.min(...acceptedInvitations);
    const resetTime = oldestAcceptance + (7 * 24 * 60 * 60);
    const remainingSeconds = resetTime - currentTime;
    
    const days = Math.floor(remainingSeconds / (24 * 60 * 60));
    const hours = Math.floor((remainingSeconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((remainingSeconds % (60 * 60)) / 60);
    
    return { days, hours, minutes };
  };

  const handleSwipe = async (otherUserId, action, invite) => {
    try {
      setActionLoading(prev => ({ ...prev, [otherUserId]: true }));
      
      if (action === 'like' && !canAcceptMoreInvitations()) {
        setError(t('notifications.errors.weeklyLimitReached'));
        return;
      }

      const swipeData = {
        uid: authState.uid,
        other_uid: otherUserId,
        action: action,
        invite: invite,
        createdAt: moment().utc().unix(),
        status: 'Response'
      };
      
      const response = await notificationService.swipe(swipeData);
      console.log('Swipe response:', response.data);
        
        if (response.data && response.data.customId && action === 'like') {
          const db = getFirestore();
          
          const conversationsRef = collection(db, 'Conversations');
          const q = query(
            conversationsRef,
            where('members', 'array-contains', authState.uid)
          );
          
          const querySnapshot = await getDocs(q);
          const existingConversation = querySnapshot.docs.find(doc => {
            const data = doc.data();
            return data.members.includes(response.data.members.find(id => id !== authState.uid));
          });

          if (!existingConversation) {
            const conversationData = {
              matches_id: response.data.customId,
              from: 'swipe',
              members: response.data.members,
              [authState.uid]: moment().utc().unix(),
              latestMessage: null,
              invite: response.data.invite,
              status: 'Active',
              updateAt: moment().utc().unix()
            };

            await setDoc(doc(db, 'Conversations', response.data.customId), conversationData);
            console.log('Conversation created for match:', response.data.customId);
          }
      }
      if (action === 'like') {
        dispatch(addAcceptedInvitation());
        
        // Update the server with the new accepted invitation timestamp
        await notificationService.updateAcceptedInvitations(
          authState.uid,
          [...(authState.acceptedInvitations || []), Math.floor(Date.now() / 1000)]
        );
      }
      // Retirer l'utilisateur de la liste appropriée

        setLikes(prevLikes => prevLikes.filter(like => like.uid !== otherUserId));

        setInvitations(prevInvites => prevInvites.filter(invite => invite.uid !== otherUserId));

      
      console.log(`Swipe ${action} recorded successfully`);
    } catch (error) {
      console.error('Error handling swipe:', error);
      setError(t('notifications.errors.general'));
    } finally {
      setActionLoading(prev => ({ ...prev, [otherUserId]: false }));
    }
  };

  const handleAcceptInvitation = (otherUserId, invite) => handleSwipe(otherUserId, 'like', invite);
  const handleDeclineInvitation = (otherUserId, invite) => handleSwipe(otherUserId, 'dislike', invite);

  const fetchLikes = async () => {
    try {
      setLoading(true);
      const response = await notificationService.getLikes(authState.uid, 1, 10);
      console.log('Likes received:', response.data);

      setLikes(response.data.likes || []);
      setInvitations(response.data.superlikes || []);
      setHasMoreLikes(response.data.likes?.length === 10);
      setHasMoreInvitations(response.data.superlikes?.length === 10);
      setLikesPage(1);
      setInvitationsPage(1);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching likes:', error);
      setLoading(false);
    }
  };

  const loadMoreLikes = useCallback(async () => {
    console.log('Loading more likes...');
    // Don't load more if we're not in the likes tab or we don't have a premium subscription
    if (activeTab !== 'likes' || !hasPremiumAccess(authState)) {
      console.log('Not in likes tab');
      return;
    }
    
    // Don't load more if we're already loading
    if (loadingMore) {
      console.log('Already loading');
      return;
    }

    try {
      setLoadingMore(true);
      console.log('Fetching likes page:', likesPage);
      
      const response = await notificationService.getLikes(authState.uid, likesPage + 1, 10);

      console.log('Response:', response.data);
      const newLikes = response.data.likes;
      
      if (!newLikes || newLikes.length === 0) {
        console.log('No more likes');
        setHasMoreLikes(false);
      } else {
        console.log('Adding new likes:', newLikes.length);
        setLikes(prev => [...prev, ...newLikes]);
        setLikesPage(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error loading more likes:', error);
      setError(t('notifications.errors.loadMore'));
    } finally {
      setLoadingMore(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [likesPage, authState.uid, activeTab, loadingMore, t]);

  const loadMoreInvitations = useCallback(async () => {
    console.log('Loading more invitations...');
    if (activeTab !== 'invitations') {
      console.log('Not in invitations tab');
      return;
    }
    
    if (loadingMore) {
      console.log('Already loading');
      return;
    }

    try {
      setLoadingMore(true);
      console.log('Fetching invitations page:', invitationsPage);
      
      const response = await notificationService.getInvitations(authState.uid, invitationsPage + 1, 10);

      console.log('Response:', response.data);
      const newInvitations = response.data.invitations;
      
      if (!newInvitations || newInvitations.length === 0) {
        console.log('No more invitations');
        setHasMoreInvitations(false);
      } else {
        console.log('Adding new invitations:', newInvitations.length);
        setInvitations(prev => [...prev, ...newInvitations]);
        setInvitationsPage(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error loading more invitations:', error);
      setError(t('notifications.errors.loadMore'));
    } finally {
      setLoadingMore(false);
    }
  }, [invitationsPage, authState.uid, activeTab, loadingMore, t]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'likes' && !hasPremiumAccess(authState)) {
      setShowSubscription(true);
    }
  };

  useEffect(() => {
    const updateContainerHeight = () => {
      // Get viewport height
      const vh = window.innerHeight;
      // Calculate container height (viewport height - header - tabs - padding)
      const newHeight = `${vh - 200}px`;
      setContainerHeight(newHeight);
    };

    // Initial calculation
    updateContainerHeight();

    // Update on resize
    window.addEventListener('resize', updateContainerHeight);
    
    // Cleanup
    return () => window.removeEventListener('resize', updateContainerHeight);
  }, []);

  useEffect(() => {
    if (authState.uid) {
      fetchLikes();
    }
  // Ne doit se déclencher que sur le changement d'utilisateur, pas sur fetchLikes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.uid]);

  if (loading) {
    return <Spin size="large" fullscreen tip={t('notifications.loading')} />;
  }

  return (
    <div className="page-wrapper has-menubar">
      <header className="header header-fixed bg-white style-2 border-0">
        <div className="container">
          <div className="header-content">
            <div className="left-content header-logo logo-lg">
              <a href="home.html">
                <img src={require('../../assets/images/fulllogo_transparent_nobuffer.png')} alt="" />
              </a>
            </div>
            <div className="mid-content">
            </div>
            <div className="right-content">
            </div>
          </div>
        </div>
      </header>

      <div className="content-body p-t50 p-b50">
        <div className="container">
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}
              {selectedUser && (
                <ProfileDetail 
                  user={selectedUser.userDetails} 
                  userLocation={userLocation}
                  onClose={() => setSelectedUser(null)} 
                />
              )}
              <div className="default-tab style-2">
                <ul className="nav nav-tabs" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button 
                      className={`nav-link ${activeTab === 'likes' ? 'active' : ''}`}
                      onClick={() => handleTabChange('likes')}
                    >
                      {t('notifications.tabs.likes', { count: likes.length })}
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button 
                      className={`nav-link ${activeTab === 'invitations' ? 'active' : ''}`}
                      onClick={() => handleTabChange('invitations')}
                    >
                      {t('notifications.tabs.invitations', { count: invitations.length })}
                    </button>
                  </li>
                </ul>
                <div className="tab-content">
                  <div className={`tab-pane fade ${activeTab === 'likes' ? 'active show' : ''}`}>
                    <div 
                      id="likes-container" 
                      style={{ 
                        height: containerHeight,
                        overflow: 'auto',
                        WebkitOverflowScrolling: 'touch', // For smooth scrolling on iOS
                        position: 'relative'
                      }}
                    >
                      <InfiniteScroll
                        dataLength={likes.length}
                        next={loadMoreLikes}
                        hasMore={hasMoreLikes && activeTab === 'likes' && hasPremiumAccess(authState)}
                        loader={<div className="text-center p-3"><Spin /></div>}
                        scrollableTarget="likes-container"
                      >
                        <div className="row g-2">
                          {likes.map((like,index) => (
                            <div key={like.uid+'_'+index} className="col-6">
                              <div className="dz-media-card style-4 notification-card">
                                <div className={`dz-media aspect-ratio-box ${!hasPremiumAccess(authState) ? 'blurred' : ''}`}>
                                  <img 
                                    src={like.userDetails.photos?.[0]?.photoUrl || '../assets/images/user/default-avatar.jpg'} 
                                    alt={like.userDetails.name}
                                    className="aspect-ratio-image"
                                  />
                                  {!hasPremiumAccess(authState) && (
                                    <div onClick={() => {setShowSubscription(true); }} className="premium-overlay">
                                      <i className="flaticon flaticon-lock"></i>
                                      <span>{t('notifications.premium.required')}</span>
                                    </div>
                                  )}
                                </div>
                                <div className={`dz-content ${!hasPremiumAccess(authState) ? 'blurred' : ''}`} >
                                  <div className="left-content" onClick={() => setSelectedUser(like)} style={{ cursor: 'pointer' }}>
                                    <h6 className="title">{like.userDetails.name}, {calculateAge(like.userDetails.doB)}</h6>
                                    <span className="about">{like.userDetails.school}</span>
                                  </div>
                                  <div 
                                    className="dz-icon ms-auto me-0"
                                    onClick={() => handleSwipe(like.uid, 'like')}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    {actionLoading[like.uid] ? (
                                      <div className="spinner heart-spinner"></div>
                                    ) : (
                                      <i className="flaticon flaticon-heart"></i>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                          {likes.length === 0 && (
                            <div className="col-12 text-center">
                              <p>{t('notifications.empty.likes')}</p>
                            </div>
                          )}
                        </div>
                      </InfiniteScroll>
                    </div>
                  </div>

                  <div className={`tab-pane fade ${activeTab === 'invitations' ? 'active show' : ''}`}>
                    {(!authState.level || authState.level === 'free') && (
                      <div className="invitation-limit-info">
                        <p>
                          {t('notifications.invitations.remaining', {
                            remaining: 2 - (authState.acceptedInvitations?.filter(
                              timestamp => timestamp > Math.floor(Date.now() / 1000) - (7 * 24 * 60 * 60)
                            ).length || 0)
                          })}
                        </p>
                        {!canAcceptMoreInvitations() && (
                          <div className="upgrade-message">
                            {t('notifications.premium.upgrade')}
                            {(() => {
                              const timeLeft = getTimeUntilNextInvitation();
                              if (timeLeft) {
                                return (
                                  <p className="time-remaining">
                                    {t('notifications.invitations.timeRemaining', {
                                      days: timeLeft.days,
                                      hours: timeLeft.hours,
                                      minutes: timeLeft.minutes
                                    })}
                                  </p>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        )}
                      </div>
                    )}
                    <div 
                      id="invitations-container" 
                      style={{ 
                        height: containerHeight,
                        overflow: 'auto',
                        WebkitOverflowScrolling: 'touch', // For smooth scrolling on iOS
                        position: 'relative'
                      }}
                    >
                      <InfiniteScroll
                        dataLength={invitations.length}
                        next={loadMoreInvitations}
                        hasMore={hasMoreInvitations && activeTab === 'invitations'}
                        loader={<div className="text-center p-3"><Spin /></div>}
                        scrollableTarget="invitations-container"
                      >
                        <div className="row g-2">
                          {invitations.map((invite, index) => (
                            <div key={invite.uid+'_'+index} className="col-6">
                              <div className="dz-media-card style-4 notification-card">
                                <div className="dz-media aspect-ratio-box">
                                  <img 
                                    src={invite.userDetails.photos?.[0]?.photoUrl || '../assets/images/user/default-avatar.jpg'} 
                                    alt={invite.userDetails.name}
                                    className="aspect-ratio-image"
                                  />
                                </div> 
                                <span className="invitation-type">{invite.invite || t('notifications.invitations.standard')}</span>
                                <div className="dz-content">
                                  <div className="left-content" onClick={() => setSelectedUser(invite)} style={{ cursor: 'pointer' }}>
                                    <h6 className="title">
                                      {invite.userDetails.name}, {calculateAge(invite.userDetails.doB)}
                                     
                                    </h6>
                                    <span className="about">{invite.userDetails.school}</span>
                                  </div>
                                  <div className="invitation-actions">
                                    <button 
                                      className="invitation-button decline-button"
                                      onClick={() => handleDeclineInvitation(invite.uid, invite.invite)}
                                      disabled={actionLoading[invite.uid]}
                                    >
                                      {actionLoading[invite.uid] ? (
                                        <div className="spinner"></div>
                                      ) : (
                                        t('notifications.actions.decline')
                                      )}
                                    </button>
                                    <button 
                                      className={`invitation-button accept-button ${!canAcceptMoreInvitations() ? 'disabled' : ''}`}
                                      onClick={() => {
                                        if (!canAcceptMoreInvitations()) {
                                          setShowSubscription(true);
                                        } else {
                                          handleAcceptInvitation(invite.uid, invite.invite);
                                        }
                                      }}
                                      // disabled={actionLoading[invite.uid] || !canAcceptMoreInvitations()}
                                    >
                                      {actionLoading[invite.uid] ? (
                                        <div className="spinner"></div>
                                      ) : (
                                        t('notifications.actions.accept')
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                          {invitations.length === 0 && (
                            <div className="col-12 text-center">
                              <p>{t('notifications.empty.invitations')}</p>
                            </div>
                          )}
                        </div>
                      </InfiniteScroll>
                    </div>
                  </div>
                </div>
              </div>
        </div>
      </div>
          
          <SubscriptionOffcanvas
            show={showSubscription}
            onClose={() => setShowSubscription(false)}
            onHide={() => setShowSubscription(false)}
            planType="gold"
            title={activeTab === 'likes' ? t('notifications.premium.seeLikesTitle') : t('notifications.premium.unlimitedInvitesTitle')}
          />
    </div>
  );
};

export default Notifications;
